import React, { useState, useEffect, useCallback } from 'react';

const TossPayment = ({ userInfo, onFail, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [tossPayments, setTossPayments] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // 토스페이먼츠 스크립트 로드 함수
  const loadTossScript = useCallback(() => {
    return new Promise((resolve, reject) => {
      // 이미 로드된 경우
      if (window.TossPayments) {
        console.log('토스페이먼츠 스크립트가 이미 로드되어 있습니다.');
        resolve();
        return;
      }

      // 이미 로딩 중인 스크립트가 있는지 확인
      const existingScript = document.querySelector('script[src="https://js.tosspayments.com/v1/payment"]');
      if (existingScript) {
        console.log('토스페이먼츠 스크립트가 이미 로딩 중입니다.');
        existingScript.onload = resolve;
        existingScript.onerror = reject;
        return;
      }

      console.log('토스페이먼츠 스크립트를 로드합니다...');
      const script = document.createElement('script');
      script.src = 'https://js.tosspayments.com/v1/payment';
      script.async = true;
      
      script.onload = () => {
        console.log('토스페이먼츠 스크립트 로드 완료');
        setScriptLoaded(true);
        resolve();
      };
      
      script.onerror = (error) => {
        console.error('토스페이먼츠 스크립트 로드 실패:', error);
        reject(new Error('토스페이먼츠 스크립트를 불러올 수 없습니다.'));
      };
      
      document.head.appendChild(script);
    });
  }, []);

  // 토스페이먼츠 초기화 함수
  const initializeTossPayments = useCallback(async () => {
    try {
      console.log('토스페이먼츠 초기화 시작...');
      await loadTossScript();
      
      // 스크립트 로드 후 약간의 지연을 두고 초기화
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (!window.TossPayments) {
        throw new Error('토스페이먼츠 객체를 찾을 수 없습니다.');
      }
      
      console.log('토스페이먼츠 초기화 중...');
      console.log('window.TossPayments:', typeof window.TossPayments);
      
      // 실제 운영용 Client Key로 변경 필요
      // 토스페이먼츠 대시보드에서 운영용 키를 확인하여 변경해주세요
      const clientKey = 'live_ck_MGjLJoQ1aVZAz7doNmP3w6KYe2RN';
      console.log('Client Key:', clientKey);
      
      const tossInstance = window.TossPayments(clientKey);
      console.log('토스페이먼츠 인스턴스 생성됨:', !!tossInstance);
      console.log('requestPayment 메서드 존재:', typeof tossInstance.requestPayment);
      
      setTossPayments(tossInstance);
      console.log('토스페이먼츠 초기화 완료');
      
    } catch (error) {
      console.error('토스페이먼츠 초기화 실패:', error);
      console.error('초기화 에러 상세:', {
        message: error.message,
        stack: error.stack
      });
      alert('결제 시스템을 초기화할 수 없습니다. 잠시 후 다시 시도해주세요.');
      throw error;
    }
  }, [loadTossScript]);

  // 컴포넌트 마운트 시 토스페이먼츠 초기화
  useEffect(() => {
    initializeTossPayments().catch(console.error);
  }, [initializeTossPayments]);

  // 결제 수단 선택 화면 표시
  const handlePayment = () => {
    setShowPaymentMethods(true);
  };

  // 실제 토스페이먼츠 결제 처리
  const handleTossPayment = async () => {
    console.log('=== 결제 버튼 클릭됨 ===');
    console.log('현재 상태:', { isLoading, tossPayments: !!tossPayments, scriptLoaded });
    
    if (isLoading) {
      console.log('이미 로딩 중입니다.');
      return;
    }
    
    setIsLoading(true);
    console.log('결제 요청 시작');

    try {
      // 토스페이먼츠가 초기화되지 않은 경우 재초기화
      if (!tossPayments) {
        console.log('토스페이먼츠 재초기화 중...');
        await initializeTossPayments();
        console.log('재초기화 완료, tossPayments 상태:', !!tossPayments);
      }

      // 다시 한번 확인
      if (!tossPayments) {
        throw new Error('토스페이먼츠가 초기화되지 않았습니다.');
      }

      const orderId = `KOIMA_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      console.log('주문 ID 생성:', orderId);
      
      // 결제 성공/실패를 처리할 콜백 URL 설정
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}?payment=success&orderId=${orderId}&email=${encodeURIComponent(userInfo?.ceoEmail || '')}`;
      const failUrl = `${baseUrl}?payment=fail&orderId=${orderId}&email=${encodeURIComponent(userInfo?.ceoEmail || '')}`;
      
      console.log('결제 파라미터:', {
        amount: 275000,
        orderId: orderId,
        orderName: 'KOIMA 연회비',
        customerName: userInfo?.ceoName || '회원',
        customerEmail: userInfo?.ceoEmail || '',
        customerMobilePhone: userInfo?.phone?.replace(/-/g, '') || '',
        successUrl: successUrl,
        failUrl: failUrl,
      });

      const paymentData = {
        amount: 275000,
        orderId: orderId,
        orderName: 'KOIMA 연회비',
        customerName: userInfo?.ceoName || '회원',
        customerEmail: userInfo?.ceoEmail || '',
        customerMobilePhone: userInfo?.phone?.replace(/-/g, '') || '',
        successUrl: successUrl,
        failUrl: failUrl,
        taxFreeAmount: 275000, // 면세 금액 설정 (전체 금액을 면세로 처리)
      };

      console.log('결제창 호출 중...');
      console.log('tossPayments 객체:', tossPayments);
      console.log('requestPayment 메서드:', typeof tossPayments.requestPayment);
      
      // 토스페이먼츠 공식 방식: requestPayment는 Promise를 반환하지 않음
      // 결제창을 열고 결제 완료 후 successUrl/failUrl로 리다이렉트됨
      tossPayments.requestPayment('카드', paymentData);
      
      // 이 코드는 실행되지 않음 - 결제창이 열린 후 리다이렉트됨
      console.log('결제창 호출 완료');
      
    } catch (error) {
      console.error('결제 실패:', error);
      console.error('에러 상세:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      // 사용자가 결제를 취소한 경우
      if (error.code === 'USER_CANCEL') {
        console.log('사용자가 결제를 취소했습니다.');
        if (onCancel) {
          onCancel();
        }
      } else {
        // 그 외의 에러
        console.error('결제 에러 상세:', error);
        alert('결제 실패: ' + (error.message || '알 수 없는 오류가 발생했습니다.'));
        if (onFail) {
          onFail(error);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 뒤로 가기
  const handleBack = () => {
    setShowPaymentMethods(false);
  };

  // 결제 수단 선택 화면
  if (showPaymentMethods) {
    return (
      <div className="space-y-4">
        {/* 뒤로 가기 버튼 */}
        <div className="flex items-center mb-4">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-800"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            뒤로 가기
          </button>
        </div>

        {/* 결제 정보 */}
        <div className="bg-blue-50 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4 text-center">결제 정보</h3>
          <div className="space-y-2 text-gray-700">
            <div className="flex justify-between">
              <span>상품명:</span>
              <span className="font-medium">KOIMA 연회비</span>
            </div>
            <div className="flex justify-between">
              <span>금액:</span>
              <span className="font-medium text-lg text-blue-600">275,000원</span>
            </div>
            <div className="flex justify-between">
              <span>결제자:</span>
              <span className="font-medium">{userInfo?.ceoName || '회원'}</span>
            </div>
          </div>
        </div>

        {/* 결제 수단 선택 */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 text-center">결제 수단 선택</h3>
          
          <div className="space-y-3">
            {/* 카드 결제 */}
            <button
              onClick={handleTossPayment}
              disabled={isLoading || !tossPayments}
              className={`w-full p-4 border-2 rounded-xl transition-all ${
                isLoading || !tossPayments
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                  : 'border-blue-200 bg-blue-50 hover:border-blue-300 hover:bg-blue-100'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">신용카드/체크카드</div>
                    <div className="text-sm text-gray-600">
                      {!tossPayments ? '결제 시스템 로딩 중...' : '토스페이먼츠'}
                    </div>
                  </div>
                </div>
                {isLoading && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                )}
              </div>
            </button>

            {/* 간편결제 (토스페이) */}
            {/*
            <button
              onClick={handleTossPayment}
              disabled={isLoading}
              className="w-full p-4 border-2 border-gray-200 bg-gray-50 rounded-xl opacity-50 cursor-not-allowed"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-sm">토스</span>
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900">토스페이</div>
                  <div className="text-sm text-gray-600">준비 중</div>
                </div>
              </div>
            </button>
            */}
          </div>
        </div>
      </div>
    );
  }

  // 기본 결제 안내 화면
  return (
    <div className="space-y-6">
      {/* 간단한 결제 정보 */}
      <div className="text-center space-y-2">
        <div className="text-2xl font-bold text-blue-600">275,000원</div>
        <div className="text-sm text-gray-600">결제자: {userInfo?.ceoName || '회원'}</div>
      </div>

      {/* 결제하기 버튼 */}
      <button
        onClick={handlePayment}
        className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
      >
        회비 카드 결제하기
      </button>

      <div className="text-center">
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          취소
        </button>
      </div>
    </div>
  );
};

export default TossPayment;
